import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Thumbnail,
  Text,
  Badge,
  Button,
  InlineStack,
  BlockStack,
  Banner,
  Pagination,
  useIndexResourceState,
  Filters,
  ChoiceList,
  Modal,
  EmptyState,
} from '@shopify/polaris';
import { ImageIcon } from '@shopify/polaris-icons';
import { useShopifyFetch, Product, ProductsResponse } from '../hooks/useShopifyFetch';

const ITEMS_PER_PAGE = 20;

export default function Products() {
  const { get, post, del } = useShopifyFetch();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncFilter, setSyncFilter] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Fetch products
  const {
    data: productsData,
    isLoading,
    error,
  } = useQuery<ProductsResponse>({
    // searchQuery is part of the key AND the request URL so typing actually
    // filters (previously it was neither, so the search box was inert).
    queryKey: ['products', page, syncFilter, searchQuery],
    queryFn: () =>
      get(
        `/api/products?limit=${ITEMS_PER_PAGE}&offset=${page * ITEMS_PER_PAGE}${
          syncFilter === 'synced' ? '&synced_only=true' : ''
        }${searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery.trim())}` : ''}`
      ),
  });

  const products = productsData?.products || [];
  const totalProducts = productsData?.total || 0;

  // Import products mutation
  const importMutation = useMutation({
    mutationFn: () => post('/api/products/import'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setImportModalOpen(false);
    },
  });

  // Sync single product mutation
  const syncMutation = useMutation({
    mutationFn: (productId: string) => post(`/api/products/${productId}/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  // Unsync single product mutation
  const unsyncMutation = useMutation({
    mutationFn: (productId: string) => del(`/api/products/${productId}/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  // Bulk sync mutation
  const bulkSyncMutation = useMutation({
    mutationFn: (productIds: string[]) =>
      post('/api/products/bulk-sync', { product_ids: productIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      clearSelection();
    },
  });

  // Resource selection
  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(products);

  // Handlers
  const handleSyncToggle = useCallback(
    (product: Product) => {
      if (product.is_synced) {
        unsyncMutation.mutate(product.id);
      } else {
        syncMutation.mutate(product.id);
      }
    },
    [syncMutation, unsyncMutation]
  );

  const handleBulkSync = useCallback(() => {
    bulkSyncMutation.mutate(selectedResources);
  }, [selectedResources, bulkSyncMutation]);

  const handleFilterChange = useCallback((value: string[]) => {
    setSyncFilter(value[0] || null);
    setPage(0);
  }, []);

  const handleFiltersQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(0); // reset to first page when the search term changes
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    setSearchQuery('');
    setSyncFilter(null);
    setPage(0);
  }, []);

  // Filter UI
  const filters = [
    {
      key: 'syncStatus',
      label: 'Sync Status',
      filter: (
        <ChoiceList
          title="Sync Status"
          titleHidden
          choices={[
            { label: 'All', value: '' },
            { label: 'Synced', value: 'synced' },
          ]}
          selected={syncFilter ? [syncFilter] : ['']}
          onChange={handleFilterChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = syncFilter
    ? [
        {
          key: 'syncStatus',
          label: `Status: ${syncFilter}`,
          onRemove: () => setSyncFilter(null),
        },
      ]
    : [];

  // Promoted bulk actions
  const promotedBulkActions = [
    {
      content: 'Sync to Marketplace',
      onAction: handleBulkSync,
      disabled: bulkSyncMutation.isPending,
    },
  ];

  // Row markup
  const rowMarkup = products.map((product, index) => (
    <IndexTable.Row
      id={product.id}
      key={product.id}
      selected={selectedResources.includes(product.id)}
      position={index}
    >
      <IndexTable.Cell>
        <Thumbnail
          source={product.image_url || ImageIcon}
          alt={product.title}
          size="small"
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold" as="span">
          {product.title}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {product.price ? `$${product.price.toFixed(2)}` : '-'}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={product.is_synced ? 'success' : 'info'}>
          {product.is_synced ? 'Synced' : 'Not Synced'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {product.last_synced_at
          ? new Date(product.last_synced_at).toLocaleDateString()
          : '-'}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Button
          size="slim"
          variant={product.is_synced ? 'plain' : 'primary'}
          onClick={() => handleSyncToggle(product)}
          loading={
            syncMutation.isPending &&
            syncMutation.variables === product.id
          }
        >
          {product.is_synced ? 'Unsync' : 'Sync'}
        </Button>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  if (error) {
    return (
      <Page title="Products">
        <Banner tone="critical">
          <p>Failed to load products. Please refresh the page.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Products"
      subtitle={`${totalProducts} products`}
      primaryAction={{
        content: 'Import Products',
        onAction: () => setImportModalOpen(true),
      }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <BlockStack>
              <div style={{ padding: '16px' }}>
                <Filters
                  queryValue={searchQuery}
                  queryPlaceholder="Search products"
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={handleFiltersQueryChange}
                  onQueryClear={() => {
                    setSearchQuery('');
                    setPage(0);
                  }}
                  onClearAll={handleFiltersClearAll}
                />
              </div>
              {!isLoading && products.length === 0 ? (
                <EmptyState
                  heading="No products yet"
                  action={{
                    content: 'Import products',
                    onAction: () => setImportModalOpen(true),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Import your Shopify products to start syncing them to the Affilync
                    marketplace and generating affiliate links.
                  </p>
                </EmptyState>
              ) : (
                <IndexTable
                  resourceName={{ singular: 'product', plural: 'products' }}
                  itemCount={products.length}
                  selectedItemsCount={
                    allResourcesSelected ? 'All' : selectedResources.length
                  }
                  onSelectionChange={handleSelectionChange}
                  headings={[
                    { title: 'Image' },
                    { title: 'Title' },
                    { title: 'Price' },
                    { title: 'Status' },
                    { title: 'Last Synced' },
                    { title: 'Actions' },
                  ]}
                  promotedBulkActions={promotedBulkActions}
                  loading={isLoading}
                >
                  {rowMarkup}
                </IndexTable>
              )}
              {totalProducts > ITEMS_PER_PAGE && (
                <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={page > 0}
                      hasNext={(page + 1) * ITEMS_PER_PAGE < totalProducts}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                  </InlineStack>
                </div>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Import Modal */}
      <Modal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title="Import Products"
        primaryAction={{
          content: 'Import All Products',
          onAction: () => importMutation.mutate(),
          loading: importMutation.isPending,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setImportModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p">
              This will import all active products from your Shopify store.
              Existing products will be updated with the latest information.
            </Text>
            <Banner tone="info">
              <p>
                Products are automatically synced when created or updated in Shopify.
                Use this to do a full initial import or refresh all products.
              </p>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
